/**
 * LOCATION: dashboard-business/src/pages/ResetPassword.js
 * Complete redesign — FA icons only, zero emojis
 * Features: show/hide password, strength bar, live requirements,
 *           confirm match, success animation, mobile responsive
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = 'https://janimony-backend.onrender.com/api';

const ResetPassword = () => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const resetToken = location.state?.resetToken;
  const email      = location.state?.email;

  const [formData, setFormData]             = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword]     = useState(false);
  const [showConfirm,  setShowConfirm]      = useState(false);
  const [loading,      setLoading]          = useState(false);
  const [error,        setError]            = useState('');
  const [success,      setSuccess]          = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

  useEffect(() => {
    if (!resetToken) navigate('/forgot-password');

    // Font Awesome
    if (!document.getElementById('fa-rp')) {
      const fa = document.createElement('link');
      fa.id = 'fa-rp'; fa.rel = 'stylesheet';
      fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
      document.head.appendChild(fa);
    }
  }, [resetToken, navigate]);

  const checkPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8)  score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password))   score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    const strengths = [
      { score: 0, text: '',            color: '' },
      { score: 1, text: 'Weak',        color: '#ef4444' },
      { score: 2, text: 'Fair',        color: '#f59e0b' },
      { score: 3, text: 'Good',        color: '#3b82f6' },
      { score: 4, text: 'Strong',      color: '#10b981' },
      { score: 5, text: 'Very Strong', color: '#059669' },
    ];
    setPasswordStrength(strengths[score] || strengths[0]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    if (name === 'password') checkPasswordStrength(value);
  };

  const requirements = [
    { met: formData.password.length >= 8,          label: 'At least 8 characters'   },
    { met: /[A-Z]/.test(formData.password),        label: 'One uppercase letter'    },
    { met: /[a-z]/.test(formData.password),        label: 'One lowercase letter'    },
    { met: /\d/.test(formData.password),           label: 'One number'              },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!/\d/.test(formData.password)) {
      setError('Password must contain at least one number');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        resetToken,
        newPassword: formData.password,
      });
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login', {
            state: { email, message: 'Password reset successful! Please login.' },
          });
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Success state ────────────────────────────────────────────
  if (success) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Syne:wght@700;800&display=swap');
          *{box-sizing:border-box;}
          .rp-page{min-height:100vh;background:linear-gradient(135deg,#0a0a0a 0%,#1a0533 55%,#0d1117 100%);display:flex;align-items:center;justify-content:center;padding:20px;font-family:'DM Sans',sans-serif;}
          .rp-orb1{position:fixed;width:420px;height:420px;border-radius:50%;background:radial-gradient(circle,rgba(16,185,129,0.15),transparent 70%);top:-140px;right:-80px;pointer-events:none;}
          .rp-orb2{position:fixed;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,rgba(102,126,234,0.1),transparent 70%);bottom:-100px;left:-60px;pointer-events:none;}
          .rp-card{width:100%;max-width:480px;background:rgba(255,255,255,0.04);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,0.1);border-radius:24px;padding:48px 44px;position:relative;z-index:1;box-shadow:0 40px 80px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.08);text-align:center;}
          .rp-logo{display:flex;align-items:center;justify-content:center;gap:10px;margin-bottom:36px;}
          .rp-logo-icon{width:42px;height:42px;border-radius:13px;background:linear-gradient(135deg,#667eea,#764ba2);display:flex;align-items:center;justify-content:center;box-shadow:0 8px 20px rgba(102,126,234,0.4);}
          .rp-logo-name{font-family:'Syne',sans-serif;font-size:20px;font-weight:800;color:#fff;}
          @keyframes rp-pop{from{transform:scale(0.5);opacity:0;}to{transform:scale(1);opacity:1;}}
          @keyframes rp-pulse{0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,0.4);}50%{box-shadow:0 0 0 16px rgba(16,185,129,0);}}
          .rp-success-icon{width:88px;height:88px;background:rgba(16,185,129,0.15);border:2px solid rgba(16,185,129,0.4);border-radius:24px;display:flex;align-items:center;justify-content:center;margin:0 auto 24px;animation:rp-pop 0.5s cubic-bezier(0.175,0.885,0.32,1.275),rp-pulse 2s 0.5s ease-in-out infinite;}
          @media(max-width:500px){.rp-card{padding:36px 22px;}}
        `}</style>
        <div className="rp-page">
          <div className="rp-orb1" /><div className="rp-orb2" />
          <div className="rp-card">
       <div className="rp-logo">
  <img 
    src={`${process.env.PUBLIC_URL}/janimony-logo.png`} 
    alt="Janimony" 
    style={{ width: '100px', height: 'auto', objectFit: 'contain' }}
  />
</div>
            <div className="rp-success-icon">
              <i className="fa-solid fa-shield-check" style={{ color: '#10b981', fontSize: 36 }}></i>
            </div>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 800, color: '#fff', margin: '0 0 12px' }}>
              Password Reset!
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, margin: '0 0 10px' }}>
              Your password has been reset successfully.<br />
              You can now log in with your new password.
            </p>
            {email && (
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>
                Account: <strong style={{ color: 'rgba(255,255,255,0.5)' }}>{email}</strong>
              </p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 24, fontSize: 13, color: 'rgba(255,255,255,0.25)' }}>
              <i className="fa-solid fa-spinner fa-spin"></i>
              Redirecting to login…
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Main form ────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Syne:wght@700;800&display=swap');
        *{box-sizing:border-box;}
        .rp-page{min-height:100vh;background:linear-gradient(135deg,#0a0a0a 0%,#1a0533 55%,#0d1117 100%);display:flex;align-items:center;justify-content:center;padding:20px;font-family:'DM Sans',sans-serif;position:relative;overflow:hidden;}
        .rp-orb1{position:fixed;width:420px;height:420px;border-radius:50%;background:radial-gradient(circle,rgba(102,126,234,0.15),transparent 70%);top:-140px;right:-80px;pointer-events:none;}
        .rp-orb2{position:fixed;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,rgba(16,185,129,0.1),transparent 70%);bottom:-100px;left:-60px;pointer-events:none;}
        .rp-card{width:100%;max-width:500px;background:rgba(255,255,255,0.04);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,0.1);border-radius:24px;padding:44px 40px;position:relative;z-index:1;box-shadow:0 40px 80px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.08);}
        .rp-logo{display:flex;align-items:center;justify-content:center;gap:10px;margin-bottom:28px;}
        .rp-logo-icon{width:42px;height:42px;border-radius:13px;background:linear-gradient(135deg,#667eea,#764ba2);display:flex;align-items:center;justify-content:center;box-shadow:0 8px 20px rgba(102,126,234,0.4);}
        .rp-logo-name{font-family:'Syne',sans-serif;font-size:20px;font-weight:800;color:#fff;}
        .rp-icon-wrap{width:68px;height:68px;border-radius:20px;background:rgba(102,126,234,0.14);border:1px solid rgba(102,126,234,0.28);display:flex;align-items:center;justify-content:center;margin:0 auto 20px;}
        .rp-title{font-family:'Syne',sans-serif;font-size:26px;font-weight:800;color:#fff;text-align:center;margin:0 0 8px;}
        .rp-sub{font-size:13px;color:rgba(255,255,255,0.4);text-align:center;line-height:1.7;margin:0 0 6px;}
        .rp-email-chip{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;background:rgba(102,126,234,0.12);border:1px solid rgba(102,126,234,0.25);border-radius:20px;font-size:12px;font-weight:700;color:#a5b4fc;margin:0 auto 28px;display:block;text-align:center;width:fit-content;}
        .rp-alert{padding:12px 15px;border-radius:10px;font-size:13px;margin-bottom:18px;display:flex;align-items:flex-start;gap:8px;line-height:1.5;background:rgba(239,68,68,0.1);border:1px solid rgba(239,68,68,0.3);color:#fca5a5;}
        .rp-grp{margin-bottom:18px;}
        .rp-lbl{display:block;font-size:11px;font-weight:700;color:rgba(255,255,255,0.4);margin-bottom:8px;letter-spacing:0.5px;text-transform:uppercase;}
        .rp-wrap{position:relative;}
        .rp-inp{width:100%;padding:13px 44px 13px 44px;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.12);border-radius:12px;color:#fff;font-size:14px;outline:none;font-family:'DM Sans',sans-serif;transition:all 0.2s;box-sizing:border-box;}
        .rp-inp::placeholder{color:rgba(255,255,255,0.22);}
        .rp-inp:focus{border-color:rgba(102,126,234,0.6);background:rgba(102,126,234,0.08);box-shadow:0 0 0 3px rgba(102,126,234,0.12);}
        .rp-ico{position:absolute;left:15px;top:50%;transform:translateY(-50%);color:rgba(255,255,255,0.3);font-size:14px;}
        .rp-eye{position:absolute;right:14px;top:50%;transform:translateY(-50%);background:transparent;border:none;cursor:pointer;color:rgba(255,255,255,0.3);font-size:14px;padding:3px;transition:color 0.2s;}
        .rp-eye:hover{color:rgba(255,255,255,0.6);}
        /* Strength bar */
        .rp-strength-wrap{margin-top:10px;}
        .rp-strength-bar-bg{height:5px;background:rgba(255,255,255,0.08);border-radius:3px;overflow:hidden;}
        .rp-strength-bar-fill{height:100%;border-radius:3px;transition:all 0.35s ease;}
        .rp-strength-txt{font-size:11px;font-weight:700;margin-top:5px;}
        /* Requirements box */
        .rp-reqs{background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.07);border-radius:12px;padding:14px 16px;margin-top:10px;}
        .rp-req{display:flex;align-items:center;gap:9px;font-size:12px;padding:3px 0;transition:color 0.2s;}
        .rp-req.met{color:#10b981;}
        .rp-req.unmet{color:rgba(255,255,255,0.28);}
        /* Match indicator */
        .rp-match{display:flex;align-items:center;gap:6px;font-size:12px;font-weight:600;margin-top:7px;}
        /* Submit button */
        .rp-btn{width:100%;padding:13px;background:linear-gradient(135deg,#10b981,#059669);color:#fff;border:none;border-radius:12px;font-size:15px;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;display:flex;align-items:center;justify-content:center;gap:8px;box-shadow:0 8px 20px rgba(16,185,129,0.35);transition:all 0.2s;margin-top:4px;}
        .rp-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 12px 28px rgba(16,185,129,0.45);}
        .rp-btn:disabled{opacity:0.6;cursor:not-allowed;}
        .rp-back{display:block;text-align:center;margin-top:20px;font-size:13px;color:rgba(102,126,234,0.85);text-decoration:none;font-weight:700;}
        .rp-back:hover{color:#a5b4fc;}
        @media(max-width:520px){.rp-card{padding:32px 20px;border-radius:20px;}.rp-title{font-size:22px;}}
        @media(max-width:380px){.rp-card{padding:26px 16px;}.rp-title{font-size:20px;}}
      `}</style>

      <div className="rp-page">
        <div className="rp-orb1" /><div className="rp-orb2" />

        <div className="rp-card">

          {/* Logo */}
        <div className="rp-logo">
  <img 
    src={`${process.env.PUBLIC_URL}/janimony-logo.png`} 
    alt="Janimony" 
    style={{ width: '80px', height: 'auto', objectFit: 'contain' }}
  />
</div>

          {/* Icon */}
          <div className="rp-icon-wrap">
            <i className="fa-solid fa-key" style={{ color: '#a5b4fc', fontSize: 28 }}></i>
          </div>

          <h1 className="rp-title">Create New Password</h1>
          <p className="rp-sub">Set a strong new password for your account.</p>

          {email && (
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <span className="rp-email-chip">
                <i className="fa-solid fa-envelope" style={{ fontSize: 11 }}></i>
                {email}
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="rp-alert">
              <i className="fa-solid fa-circle-exclamation" style={{ flexShrink: 0, marginTop: 1 }}></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>

            {/* ── New Password ── */}
            <div className="rp-grp">
              <label className="rp-lbl">New Password</label>
              <div className="rp-wrap">
                <i className="fa-solid fa-lock rp-ico"></i>
                <input
                  className="rp-inp"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  required
                  autoFocus
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="rp-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>

              {/* Strength bar */}
              {formData.password && (
                <div className="rp-strength-wrap">
                  <div className="rp-strength-bar-bg">
                    <div
                      className="rp-strength-bar-fill"
                      style={{
                        width:      `${(passwordStrength.score / 5) * 100}%`,
                        background: passwordStrength.color || '#e5e7eb',
                      }}
                    />
                  </div>
                  {passwordStrength.text && (
                    <p className="rp-strength-txt" style={{ color: passwordStrength.color }}>
                      <i className="fa-solid fa-shield-halved" style={{ marginRight: 5 }}></i>
                      {passwordStrength.text}
                    </p>
                  )}
                </div>
              )}

              {/* Requirements checklist */}
              <div className="rp-reqs">
                {requirements.map(r => (
                  <div key={r.label} className={`rp-req ${r.met ? 'met' : 'unmet'}`}>
                    <i
                      className={`fa-solid ${r.met ? 'fa-circle-check' : 'fa-circle'}`}
                      style={{ fontSize: 11, flexShrink: 0 }}
                    ></i>
                    {r.label}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Confirm Password ── */}
            <div className="rp-grp">
              <label className="rp-lbl">Confirm New Password</label>
              <div className="rp-wrap">
                <i className="fa-solid fa-lock rp-ico"></i>
                <input
                  className="rp-inp"
                  type={showConfirm ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  required
                  autoComplete="new-password"
                  style={{
                    borderColor: formData.confirmPassword
                      ? formData.password === formData.confirmPassword
                        ? 'rgba(16,185,129,0.5)'
                        : 'rgba(239,68,68,0.5)'
                      : undefined,
                  }}
                />
                <button
                  type="button"
                  className="rp-eye"
                  onClick={() => setShowConfirm(!showConfirm)}
                  tabIndex={-1}
                >
                  <i className={`fa-solid ${showConfirm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>

              {/* Match indicator */}
              {formData.confirmPassword && (
                <p
                  className="rp-match"
                  style={{
                    color: formData.password === formData.confirmPassword
                      ? '#10b981'
                      : '#fca5a5',
                  }}
                >
                  <i
                    className={`fa-solid ${formData.password === formData.confirmPassword ? 'fa-circle-check' : 'fa-circle-xmark'}`}
                    style={{ fontSize: 12 }}
                  ></i>
                  {formData.password === formData.confirmPassword
                    ? 'Passwords match'
                    : 'Passwords do not match'}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="rp-btn"
              disabled={loading}
            >
              {loading
                ? <><i className="fa-solid fa-spinner fa-spin"></i>Resetting Password…</>
                : <><i className="fa-solid fa-shield-check"></i>Reset Password</>}
            </button>
          </form>

          {/* Back link */}
          <Link to="/login" className="rp-back">
            <i className="fa-solid fa-arrow-left" style={{ marginRight: 6, fontSize: 11 }}></i>
            Back to Login
          </Link>

        </div>
      </div>
    </>
  );
};

export default ResetPassword;